import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { OfflineProvider } from "@/lib/OfflineContext";
import { ThemeProvider } from "@/lib/ThemeContext";

// Pages
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import ProjectsPage from "@/pages/ProjectsPage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import VisitPage from "@/pages/VisitPage";
import AdminPage from "@/pages/AdminPage";
import AccountPage from "@/pages/AccountPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import LegalNoticePage from "@/pages/LegalNoticePage";
import CompteRenduPage from "@/pages/CompteRenduPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// API instance with auth
export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false, superAdminOnly = false, companyOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (superAdminOnly && user.role !== "super_admin") {
    return <Navigate to="/dashboard" replace />;
  }

  if (adminOnly && user.role !== "admin_entreprise") {
    return <Navigate to="/dashboard" replace />;
  }

  // Block super_admin from company operational routes
  if (companyOnly && user.role === "super_admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Verify token is still valid
      api.get("/auth/me")
        .then((res) => {
          setUser(res.data);
          localStorage.setItem("user", JSON.stringify(res.data));
        })
        .catch(() => {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      const res = await api.post("/auth/login", { email, password });
      const { access_token, user: userData } = res.data;
      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);
      toast.success("Connexion réussie");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de connexion");
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("sa_token");
    localStorage.removeItem("sa_user");
    setUser(null);
    toast.success("Déconnexion réussie");
  };

  const impersonate = async (userId) => {
    try {
      // Save super admin credentials before impersonating
      localStorage.setItem("sa_token", localStorage.getItem("token"));
      localStorage.setItem("sa_user", localStorage.getItem("user"));

      const res = await api.post(`/auth/impersonate/${userId}`);
      const { access_token, user: userData } = res.data;
      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);
      toast.success(`Connecté en tant que ${userData.first_name || userData.email}`);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur d'impersonation");
      return false;
    }
  };

  const stopImpersonating = async () => {
    try {
      const res = await api.post("/auth/stop-impersonation");
      const { access_token, user: userData } = res.data;
      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(userData));
      localStorage.removeItem("sa_token");
      localStorage.removeItem("sa_user");
      setUser(userData);
      toast.success("Retour au compte super admin");
      return true;
    } catch (error) {
      // Fallback: restore from localStorage
      const saToken = localStorage.getItem("sa_token");
      const saUser = localStorage.getItem("sa_user");
      if (saToken && saUser) {
        localStorage.setItem("token", saToken);
        localStorage.setItem("user", saUser);
        localStorage.removeItem("sa_token");
        localStorage.removeItem("sa_user");
        setUser(JSON.parse(saUser));
        toast.success("Retour au compte super admin");
        return true;
      }
      toast.error("Erreur lors du retour");
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, impersonate, stopImpersonating }}>
      {children}
    </AuthContext.Provider>
  );
};

function App() {
  return (
    <div className="App">
      <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <OfflineProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/legal" element={<LegalNoticePage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects"
              element={
                <ProtectedRoute companyOnly>
                  <ProjectsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:projectId"
              element={
                <ProtectedRoute companyOnly>
                  <ProjectDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/visits/:visitId"
              element={
                <ProtectedRoute companyOnly>
                  <VisitPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/comptes-rendus/:crId"
              element={
                <ProtectedRoute companyOnly>
                  <CompteRenduPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute adminOnly>
                  <AdminPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/super-admin"
              element={
                <ProtectedRoute superAdminOnly>
                  <SuperAdminPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <AccountPage />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </OfflineProvider>
        </AuthProvider>
      </BrowserRouter>
      </ThemeProvider>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
